/**
 * cmTableStyle
 *
 * Unified helpers for the CM-table "styling field" convention shared by point,
 * polygon and linestring tables.
 *
 * Convention (all geometries):
 *   - A single field drives per-feature styling. Its value lives in `row_data`
 *     and is flattened by the backend into `feature.properties`.
 *   - The style expression (`map_display_config.style`) selects the icon/color
 *     via a MapLibre `["match", ["get", <key>], value, output, ..., default]`.
 *   - The relevant style property depends on geometry:
 *       point       → `iconStyle` (icon image path)
 *       polygon     → `fill-color` (color)
 *       linestring  → `line-color` (color)
 *
 * The style expression itself is the source of truth for BOTH the styling key
 * (the `["get", <key>]`) and the selectable choices (the match arms), so the
 * form never has to guess from `field_schema` (whose field name may differ from
 * the styled key).
 */

export type CmSpatialType = "point" | "multipoint" | "polygon" | "multipolygon" | "linestring" | "multilinestring" | "none";

/** A single selectable styling choice derived from a style `match` expression. */
export interface CmStyleChoice {
  /** The value stored in `row_data[<key>]` (e.g. "Camp", "Park"). */
  value: string;
  /** Icon image path for point tables (e.g. "icons/Camp.png"). */
  icon?: string;
  /** Color for polygon/linestring tables (e.g. "#22c55e"). */
  color?: string;
}

/** The default styling field key when a style has no explicit `["get", key]`. */
export const DEFAULT_STYLE_FIELD = "type";

/** Returns the base geometry (collapses multi-variants). */
export function baseGeometry(spatialType?: CmSpatialType | string | null): "point" | "polygon" | "linestring" | "none" {
  switch (spatialType) {
    case "point":
    case "multipoint":
      return "point";
    case "polygon":
    case "multipolygon":
      return "polygon";
    case "linestring":
    case "multilinestring":
      return "linestring";
    default:
      return "none";
  }
}

/** Whether this geometry is styled by icon (point) vs color (polygon/line). */
export function isIconStyled(spatialType?: CmSpatialType | string | null): boolean {
  return baseGeometry(spatialType) === "point";
}

/**
 * The style property that carries the per-feature `match` expression for the
 * given geometry: `iconStyle` (point), `fill-color` (polygon), `line-color` (line).
 */
export function styleMatchProperty(spatialType?: CmSpatialType | string | null): "iconStyle" | "fill-color" | "line-color" | null {
  switch (baseGeometry(spatialType)) {
    case "point":
      return "iconStyle";
    case "polygon":
      return "fill-color";
    case "linestring":
      return "line-color";
    default:
      return null;
  }
}

/** True if the value is a MapLibre `["match", ["get", key], ...]` expression. */
function isMatchExpression(expr: any): expr is any[] {
  return Array.isArray(expr) && expr[0] === "match" && Array.isArray(expr[1]) && expr[1][0] === "get";
}

/**
 * Reads the styling field key from a table's style, e.g. the `type` in
 * `["match", ["get", "type"], ...]`. Falls back to {@link DEFAULT_STYLE_FIELD}.
 */
export function getStyleFieldKey(style: Record<string, any> | undefined, spatialType?: CmSpatialType | string | null): string {
  const prop = styleMatchProperty(spatialType);
  const expr = prop ? style?.[prop] : undefined;
  if (isMatchExpression(expr)) {
    const key = expr[1][1];
    if (typeof key === "string" && key) return key;
  }
  return DEFAULT_STYLE_FIELD;
}

/**
 * Parses the selectable choices (value + icon/color) from a table's style
 * `match` expression. Grouped arms (`[["A","B"], output]`) expand to one choice
 * per value. Returns [] when the style has no match expression.
 */
export function getStyleChoices(style: Record<string, any> | undefined, spatialType?: CmSpatialType | string | null): CmStyleChoice[] {
  const prop = styleMatchProperty(spatialType);
  const expr = prop ? style?.[prop] : undefined;
  if (!isMatchExpression(expr)) return [];

  const icon = isIconStyled(spatialType);
  const choices: CmStyleChoice[] = [];

  // Arms are [value, output] pairs between index 2 and length-1 (last is default).
  for (let i = 2; i < expr.length - 1; i += 2) {
    const rawValue = expr[i];
    const output = expr[i + 1];
    const values = Array.isArray(rawValue) ? rawValue : [rawValue];
    for (const v of values) {
      if (typeof v !== "string") continue;
      choices.push(icon ? { value: v, icon: String(output) } : { value: v, color: String(output) });
    }
  }
  return choices;
}

/** The default (fallback) output — the last element of the match expression. */
export function getStyleDefaultOutput(
  style: Record<string, any> | undefined,
  spatialType?: CmSpatialType | string | null
): string | undefined {
  const prop = styleMatchProperty(spatialType);
  const expr = prop ? style?.[prop] : undefined;
  if (!isMatchExpression(expr)) return undefined;
  const last = expr[expr.length - 1];
  return typeof last === "string" ? last : undefined;
}

/**
 * Resolves the icon (point) or color (polygon/line) for a given styling value,
 * falling back to the style's default output when the value has no arm.
 */
export function resolveStyleOutputForValue(
  style: Record<string, any> | undefined,
  spatialType: CmSpatialType | string | null | undefined,
  value: string | undefined | null
): string | undefined {
  const choices = getStyleChoices(style, spatialType);
  const match = value != null ? choices.find((c) => c.value === value) : undefined;
  const output = isIconStyled(spatialType) ? match?.icon : match?.color;
  return output ?? getStyleDefaultOutput(style, spatialType);
}

/** The output (icon path / color) carried by a choice for the given geometry. */
export function choiceOutput(choice: CmStyleChoice, spatialType?: CmSpatialType | string | null): string {
  return (isIconStyled(spatialType) ? choice.icon : choice.color) ?? "";
}

/**
 * Builds a MapLibre `["match", ["get", key], value, output, ..., default]`
 * expression from a flat list of {@link CmStyleChoice}. This is the inverse of
 * {@link getStyleChoices} and keeps one arm per value (no grouping) so the shape
 * round-trips predictably through the editor.
 */
export function buildStyleMatchExpression(
  fieldKey: string,
  choices: CmStyleChoice[],
  defaultOutput: string,
  spatialType?: CmSpatialType | string | null
): any[] {
  const expr: any[] = ["match", ["get", fieldKey]];
  for (const c of choices) {
    if (!c.value) continue;
    expr.push(c.value, choiceOutput(c, spatialType));
  }
  expr.push(defaultOutput);
  return expr;
}

/**
 * Returns a new `map_display_config.style` object with the geometry's match
 * property (`iconStyle` / `fill-color` / `line-color`) rebuilt from `choices`.
 * The previous default output is preserved when present.
 */
export function buildUpdatedStyle(
  style: Record<string, any> | undefined,
  spatialType: CmSpatialType | string | null | undefined,
  fieldKey: string,
  choices: CmStyleChoice[]
): Record<string, any> {
  const prop = styleMatchProperty(spatialType);
  const next: Record<string, any> = { ...(style ?? {}) };
  if (!prop) return next;
  const defaultOutput = getStyleDefaultOutput(style, spatialType) ?? (isIconStyled(spatialType) ? "" : "#888888");
  next[prop] = buildStyleMatchExpression(fieldKey, choices, defaultOutput, spatialType);
  return next;
}

/**
 * Returns a new `field_schema` array whose entry for `fieldKey` has its
 * `choices` set to `values` (deduped). If no field matches `fieldKey`, one is
 * appended so the styling field is always represented in the schema.
 */
export function buildUpdatedFieldSchema<T extends { name: string; choices?: string[]; [k: string]: any }>(
  fieldSchema: T[] | undefined,
  fieldKey: string,
  values: string[]
): T[] {
  const uniqueValues = Array.from(new Set(values.filter(Boolean)));
  const schema = Array.isArray(fieldSchema) ? [...fieldSchema] : [];
  const idx = schema.findIndex((f) => f.name === fieldKey);
  if (idx >= 0) {
    schema[idx] = { ...schema[idx], choices: uniqueValues };
  } else {
    schema.push({ name: fieldKey, type: "string", label: fieldKey, choices: uniqueValues } as unknown as T);
  }
  return schema;
}
